import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { FreebetsIcon } from '../../../../assets/img/betting';
import { Target } from '../../../../client';
import Formatter from '../../../../i18n/formatter';
import { getCurrency, __ } from '../../../../i18n/l10n';
import theme from '../../../../styled-components/dark';
import { MissionCardComplexeStatuses, type MissionCardComplexeStatus } from '../../types/components';
import { type AchievementReward } from '../../types/state/achievements';
import MissionDetailsAchievementProgressContainer from '../MissionDetailsAchievementProgress/MissionDetailsAchievementProgressContainer';

type WrapperType = {
  $isWon: boolean;
};

const Wrapper = styled.div<WrapperType>`
  position: relative;
  width: 100%;

  color: white;
  background-color: ${theme.MissionDetailsAchievement.backgroundColor};
  padding: 0.67em 1em;
  border-radius: 1em;
  overflow: hidden;

  > *:not(:last-child) {
    margin-bottom: 0.5em;
  }

  &:after {
    content: '';
    position: absolute;
    height: 100%;
    width: 0.4em;
    left: 0;
    top: 0;

    background: ${theme.MissionDetailsAchievement.afterDefaultBackgroundColor};
    transition: background 300ms ease-in-out;
  }

  ${({ $isWon }) => {
    if ($isWon) {
      return css`
        &:after {
          background-color: ${theme.MissionDetailsAchievement.afterWonBackgroundColor};
        }
      `;
    }
  }}

  ${({ onClick }) => {
    if (onClick) {
      return css`
        cursor: pointer;
        user-select: none;
        transition: filter 200ms ease-in-out;

        ${!Target.__MOBILE__
          ? css`
              &:hover {
                filter: brightness(120%);
              }
            `
          : css`
              &:active {
                filter: brightness(120%);
              }
            `}
      `;
    }
  }}
`;

const Description = styled.div`
  font-size: 90%;
`;

const RequirementRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  > *:not(:last-child) {
    margin-right: 0.5em;
  }
`;

const SimpleRequirementTag = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  background-color: ${theme.MissionDetailsAchievement.SRTBackgroundColor};
  border-radius: 0.3em;
  padding: 0 0.5em;
`;

const SRTLabel = styled.div`
  color: ${theme.MissionDetailsAchievement.SRTColor};
  font-size: 90%;
  font-weight: normal;
`;

const SRTValue = styled.div`
  font-size: 105%;
  font-weight: bold;
`;

type SpaceBetweenRowType = {
  $showProgress: boolean;
  $showRewards: boolean;
};

const SpaceBetweenRow = styled.div<SpaceBetweenRowType>`
  display: flex;
  align-items: flex-end;

  ${({ $showProgress, $showRewards }) => css`
    justify-content: ${!$showProgress ? 'flex-end' : 'space-between'};
    margin-top: ${!$showProgress && $showRewards ? -2.1 : 0}em;
  `}
`;

const Reward = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const RewardIcon = styled.img`
  width: 1.3em;
  margin-left: 0.3em;
`;

const RewardValue = styled.div`
  font-size: 120%;
`;

const StyledMissionDetailsAchievementProgressContainer = styled(MissionDetailsAchievementProgressContainer)`
  width: 100%;
`;

type MissionDetailsAchievementProps = {
  className?: string;
  missionId: number;
  achievementId: number;
  description?: string;
  minBetOdds?: number;
  minPlacedAmount?: number;
  isWon: boolean;
  reward?: AchievementReward | null;
  showProgress: boolean;
  missionStatus?: MissionCardComplexeStatus | null;
  onClick?: () => void;
};

const MissionDetailsAchievement = ({
  className = '',
  missionId,
  achievementId,
  description,
  minBetOdds,
  minPlacedAmount,
  isWon,
  reward,
  showProgress,
  missionStatus,
  onClick,
}: MissionDetailsAchievementProps) => {
  const hasValidDescription = !!description;
  const hasValidMinBetOdds = typeof minBetOdds === 'number' && (minBetOdds ?? 1) > 1;
  const hasValidMinPlacedAmount = typeof minPlacedAmount === 'number' && !!minPlacedAmount;
  const hasValidRequirementRow = hasValidMinBetOdds || hasValidMinPlacedAmount;
  const displayedRewardAmount = useMemo(
    () =>
      typeof reward?.rewardParams?.amount === 'number'
        ? Formatter.formatBankroll(
            (reward?.rewardParams?.amount ?? 0) * 100,
            getCurrency(),
            !((reward?.rewardParams?.amount ?? 0) % 1),
          )
        : null,
    [reward],
  );
  const hasValidSpaceBetweenRow = !!displayedRewardAmount || showProgress;
  const hasContent = hasValidDescription || hasValidRequirementRow || hasValidSpaceBetweenRow;

  const missionIsExpired = useMemo(
    () =>
      missionStatus === MissionCardComplexeStatuses.EXPIRED ||
      missionStatus === MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS,
    [missionStatus],
  );

  return hasContent ? (
    <Wrapper className={className} $isWon={isWon} onClick={onClick}>
      {hasValidDescription ? <Description>{description}</Description> : null}
      {hasValidRequirementRow ? (
        <RequirementRow>
          {hasValidMinBetOdds && !missionIsExpired ? (
            <SimpleRequirementTag>
              <SRTLabel>{__('Min. odds:')}</SRTLabel>&nbsp;
              <SRTValue>{Formatter.formatOdd(minBetOdds)}</SRTValue>
            </SimpleRequirementTag>
          ) : null}
          {hasValidMinPlacedAmount && !missionIsExpired ? (
            <SimpleRequirementTag>
              <SRTLabel>{__('Min. wager:')}</SRTLabel>&nbsp;
              <SRTValue>
                {Formatter.formatBankroll(minPlacedAmount, getCurrency(), !(((minPlacedAmount ?? 0) / 100) % 1))}
              </SRTValue>
            </SimpleRequirementTag>
          ) : null}
        </RequirementRow>
      ) : null}
      <SpaceBetweenRow $showRewards={!!displayedRewardAmount} $showProgress={showProgress}>
        {showProgress ? (
          <StyledMissionDetailsAchievementProgressContainer missionId={missionId} achievementId={achievementId} />
        ) : null}
        {displayedRewardAmount ? (
          <Reward>
            {displayedRewardAmount !== null ? <RewardValue>{displayedRewardAmount}</RewardValue> : null}
            <RewardIcon src={FreebetsIcon} />
          </Reward>
        ) : null}
      </SpaceBetweenRow>
    </Wrapper>
  ) : null;
};

export default MissionDetailsAchievement;
